import { Button, Table, Spinner, Pagination, Form } from "react-bootstrap";
import { resolvePath, wrapArray } from "./utils";
import { createContext, useContext, useMemo, useState } from "react";
import AutoSelectInput from "./form/AutoSelectInput";

const DataTableContext = createContext({});

/**
 * @param {Object} props
 * @param {Object[]} props.data
 * @param {boolean} props.loading
 * @param {boolean} props.paginate
 * @param {number[]} props.pageSizes
 * @param {number} props.defaultPageSize
 * @param {import("react").CSSProperties} props.style
 * @param {string} props.className
 */
function DataTable({ data = [], paginate, pageSizes = [10, 20, 50], defaultPageSize = pageSizes[0], loading, style, className = "", children = [] }) {
    children = wrapArray(children);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [sortingBy, setSortingBy] = useState(null);
    const currentData = useMemo(() => sortingBy ? data.toSorted((a, b) => sort(a, b, sortingBy)) : data, [data, sortingBy]);
    const contextValue = useMemo(() => ({ onSort, sortingBy, loading }), [sortingBy, loading]);
    const { paginated, pageNumbers, totalPages, page, setPage } = usePagination(currentData, pageSize);

    const messages = children.filter(c => c.type === Message || c.type.name === "Message");
    const shownMessage = loading ? null : messages.find(c => c.props.condition)?.props.children;
    const columns = children.filter(c => c.type === Column || c.type.name === "Column");

    function onSort(id, sortByFn) {
        if (sortingBy?.id === id) {
            if (sortingBy.order === "ascending") {
                setSortingBy({ id, order: "descending", sortByFn });
            } else {
                setSortingBy(null);
            }
        } else {
            setSortingBy({ id, order: "ascending", sortByFn });
        }
    }

    function sort(a, b, { id, order, sortByFn }) {
        const aValue = sortByFn(resolvePath(a, id) ?? a);
        const bValue = sortByFn(resolvePath(b, id) ?? b);
        const sortValue = typeof aValue === "number" ? aValue - bValue : typeof aValue === "boolean" ? (aValue ? 1 : 0) - (bValue ? 1 : 0) : aValue?.localeCompare?.(bValue);
        return sortValue * (order === "ascending" ? 1 : -1);
    }

    return <DataTableContext.Provider value={contextValue}>
        {shownMessage || <div className={`table-responsive ${className}`} style={style}>
            <Table striped bordered size="sm">
                <thead>
                    <tr>
                        {children}
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr><td colSpan={columns.length}><Spinner as="span" className="my-5" animation="border" /></td></tr>
                    ) : (paginate ? paginated : currentData).map((row, i) => (
                        <tr key={i}>
                            {columns.map((col, i) => (
                                <td className={`${col.props.showAt ? `d-none d-${col.props.showAt}-table-cell` : ""} ${col.props.className ?? ""}`} style={col.props.style ?? {}} key={i}>
                                    <span className={col.props.flex ? "d-flex justify-content-center align-items-center gap-2 border-start-0 border-end-0" : ""}>
                                        {col.props.id ? (col.props.format ?? (v => v))(resolvePath(row, col.props.id), row) : col.props.value?.(row) ?? ""}
                                    </span>
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>}

        {paginate && <div className="grid grid-rows-2 grid-cols-1 gap-4 md:grid-rows-1 md:grid-cols-3 md:gap-0 mt-3">
            <div className="items-center gap-2 invisible hidden md:flex">
                <AutoSelectInput options={pageSizes} initial={defaultPageSize} update={size => setPageSize(size)} /> per page
            </div>
            <div className="flex items-center gap-2 mx-auto">
                <Pagination.First className="list-unstyled" disabled={page === 1} onClick={() => setPage(1)} />
                <Pagination.Prev className="list-unstyled" disabled={page === 1} onClick={() => setPage(page - 1)} />
                <span>Page</span>
                <Form.Select className={`text-lg rounded-sm border-neutral-300 border-b-neutral-200 inset-shadow-sm inset-shadow-neutral-200`} value={page} onChange={e => setPage(e.target.value)} disabled={totalPages === 1}>
                    {pageNumbers.map(o => <option key={o} value={o}>{o}</option>)}
                </Form.Select>
                <Pagination.Next className="list-unstyled" disabled={page === totalPages} onClick={() => setPage(page + 1)} />
                <Pagination.Last className="list-unstyled" disabled={page === totalPages} onClick={() => setPage(totalPages)} />
            </div>
            {data.length > pageSizes[0] && <div className="flex items-center gap-2 ml-auto mx-auto md:me-0">
                <AutoSelectInput className="mx-0" options={pageSizes} initial={defaultPageSize} update={size => setPageSize(size)} /> <span className="text-nowrap">per page</span>
            </div>}
        </div>}
    </DataTableContext.Provider>;
}

/**
 * @param {Object} props
 * @param {string} props.id
 * @param {(row: any) => string} props.value
 * @param {(value: any) => string} props.format
 * @param {string} props.className
 * @param {React.CSSProperties} props.style
 * @param {string} props.showAt
 * @param {boolean} props.sortable
 * @param {boolean} props.flex
 * @param {string} props.sortId
 * @param {(value: any) => any} props.sortBy
 */
function Column({ showAt, children, id, sortable, sortId = id, sortBy = v => v }) {
    const { onSort, sortingBy, loading } = useContext(DataTableContext);
    const caretStyle = { fontSize: "0.8rem", height: "10px" };
    const columnOrder = sortId && sortingBy?.id === sortId ? sortingBy.order : null;

    return (
        <th className={(showAt ? `d-none d-${showAt}-table-cell` : "") + " text-nowrap"}>
            <span className="d-flex flex-nowrap justify-content-center">
                <span onClick={() => sortable && !loading && onSort(sortId, sortBy)} className="user-select-none" style={sortable ? { cursor: "pointer" } : {}}>
                    {children}
                </span>
                {sortable && <span onClick={() => !loading && onSort(sortId, sortBy)} className="d-flex flex-column w-fit ms-1 ngf-red" style={{ translate: "0 -2px", cursor: "pointer" }}>
                    <i className={`bi bi-caret-up${columnOrder === "ascending" ? "-fill" : ""} w-fit`} style={caretStyle} />
                    <i className={`bi bi-caret-down${columnOrder === "descending" ? "-fill" : ""} w-fit`} style={caretStyle} />
                </span>}
            </span>
        </th>
    );
}

function Message({ condition }) { return null; }

function Action({ onClick, manualSpinner = false, children }) {
    const [loading, setLoading] = useState(false);

    async function onActionClick(e) {
        if (!manualSpinner) {
            setLoading(true);
        }

        try {
            await onClick?.(e, { setLoading });
        } finally {
            setLoading(false);
        }
    }

    return (
        loading ? (
            <Spinner animation="border" className="align-middle mx-2" style={{ width: "1.5rem", height: "1.5rem" }} />
        ) : (
            <Button variant="link" tabIndex={onClick ? undefined : "-1"} className="p-0 mx-2 text-decoration-none" onClick={onActionClick}>{children}</Button>
        )
    );
}

function usePagination(items, pageSize = 10) {
    const [page, setPage] = useState(1);

    const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
    const paginated = items.slice((page - 1) * pageSize, page * pageSize);
    const pageNumbers = Array(totalPages).fill().map((_, i) => i + 1);

    if (page > totalPages) {
        setPage(totalPages);
    }

    return { page, pageNumbers, setPage: p => setPage(Math.min(Math.max(0, p), totalPages)), paginated, totalPages };
}

DataTable.Column = Column;
DataTable.Message = Message;
DataTable.Action = Action;
export default DataTable;